import { addDays } from 'date-fns/addDays'
import { isBefore } from 'date-fns/isBefore'

import type { FlowValues } from '@flows/core/types/stepTypes'
import type { ProductKey } from '@flows/insurance/types/productKeys'
import { createSafeDate } from '@flows/insurance/utils/dateUtils'

const MARKET_TIME_ZONE = 'Europe/Berlin'

const MIN_START_DAYS_FROM_TODAY: Partial<Record<ProductKey, number>> = {
  dental_v1_getsafe_de: 1,
}

const marketDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: MARKET_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const getMarketToday = (now: Date): Date => {
  const { year, month, day } = Object.fromEntries(
    marketDateFormatter.formatToParts(now).map(({ type, value }) => [type, value])
  )

  return createSafeDate(year, month, day)
}

const getMinStartDaysFromToday = (productKey: string): number =>
  MIN_START_DAYS_FROM_TODAY[productKey as ProductKey] ?? 0

export const getEarliestStartDate = (productKey: string, now: Date = new Date()): Date =>
  addDays(getMarketToday(now), getMinStartDaysFromToday(productKey))

interface StartDateParts {
  year?: string | number
  month?: string | number
  day?: string | number
}

export const getRequestedStartDate = (
  flowValues: FlowValues,
  productKey: string
): Date | undefined => {
  const startDates = flowValues.startDates as Record<string, StartDateParts | undefined> | undefined
  const { year, month, day } = startDates?.[productKey] ?? {}

  return year ? createSafeDate(year, month, day) : undefined
}

export const clampToEarliestStartDate = (
  requestedStartDate: Date | undefined,
  productKey: string,
  now: Date = new Date()
): Date => {
  const earliestStartDate = getEarliestStartDate(productKey, now)

  if (!requestedStartDate || isBefore(requestedStartDate, earliestStartDate)) {
    return earliestStartDate
  }

  return requestedStartDate
}
